/**
 * 📬 Notification Read-State Orchestration
 *
 * Single home for badge and Notification-Centre clearing logic. Cedar
 * follows the messaging-app "precise tracking" pattern (iMessage, WhatsApp,
 * Slack, Gmail) — the iOS app-icon badge reflects the backend's true
 * unread count, and each screen clears only the unread content it owns.
 *
 * We explicitly do NOT follow the social-feed "optimistic clear" pattern
 * (Instagram, X, Facebook) where opening the app zeroes the badge
 * regardless of content. That pattern erodes badge signal value over time
 * — users learn the badge lies — and breaks entirely once multiple
 * unread categories exist. Paul's 2026-04-19 report ("icon shows (3) but
 * nothing's waiting when I open") was the tip of that iceberg: the
 * frontend was zeroing the badge locally without telling the backend, so
 * the next push just restored the stale count.
 *
 * Two exports today:
 *
 *   syncBadgeToBackend()
 *       Called on app foreground. Queries /api/notifications/unread-count
 *       and sets the iOS badge to that value. Never hardcodes 0. On
 *       failure, leaves the badge unchanged — a stale-but-possibly-correct
 *       number degrades more gracefully than an actively-lied-about zero.
 *
 *   markChatAsRead()
 *       Called from every chat-screen entry point (focus / mount / web
 *       visibility). Emits three coordinated side effects:
 *         1. POST /api/notifications/mark-chat-read — advances
 *            users.chat_last_read_at so subsequent push payloads carry
 *            accurate badge counts.
 *         2. Notifications.setBadgeCountAsync(0) — immediate visual clear
 *            on iOS. Safe because step 1 has just told the backend there
 *            is nothing unread; the zero is truthful, not fabricated.
 *         3. Notifications.dismissAllNotificationsAsync() — clears
 *            delivered notifications from iOS Notification Centre.
 *            Matches iMessage's "open the conversation, its notifications
 *            go away" behaviour.
 *
 * Future unread categories — plan updates, insights, health alerts, etc.
 * — each get their own markFooAsRead() here. The architectural rule is:
 * one function per screen-owned unread category; the app icon badge is
 * the aggregate count reflected by the backend's unread-count endpoint.
 *
 * Pinned by tests/gate/test_notification_read_state.py.
 */

import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import { API_BASE_URL } from '../utils/api';
import { secureTokenStorage } from './secureTokenStorage';
import { fetchWithAuth } from './fetchWithAuth';

/**
 * Query the backend's true unread count and sync the iOS app-icon badge
 * to it. Call from AppState 'active' so the badge is truthful the moment
 * the user returns to the app.
 *
 * On any failure (no token, network error, non-2xx, malformed body) the
 * badge is left at its prior value. Falling back to a hardcoded zero
 * would defeat the entire point of Pattern B.
 *
 * Web: no-op (web has no app-icon badge API surface).
 */
export async function syncBadgeToBackend(): Promise<void> {
  if (Platform.OS === 'web') return;

  try {
    const token = await secureTokenStorage.getCedarJwt();
    if (!token) return;

    const res = await fetch(`${API_BASE_URL}/api/notifications/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;

    const data = await res.json();
    const count = typeof data?.count === 'number' ? data.count : 0;
    await Notifications.setBadgeCountAsync(count);
  } catch {
    // Intentional no-op: leave the badge at its previous value rather
    // than fabricate a zero. A stale-but-possibly-correct number is
    // better than an active lie.
  }
}

/**
 * Mark the chat conversation as read and clear the iOS badge +
 * Notification-Centre state that the chat owns. Call from every chat-
 * screen entry point (useFocusEffect on native, mount + visibility on
 * web).
 *
 * Platform behaviour:
 *   - Both platforms: POST /api/notifications/mark-chat-read so the
 *     backend advances chat_last_read_at.
 *   - Native only: setBadgeCountAsync(0) + dismissAllNotificationsAsync
 *     for the iOS Notification-Centre cleanup. (Web has no equivalent
 *     APIs; the mark-read POST alone is enough for the web service-worker
 *     badge, if/when that is added.)
 *
 * Pattern B ordering contract:
 *   The native-side badge + Notification-Centre clears run ONLY when the
 *   backend POST has returned 2xx. If the POST fails (4xx, 5xx, network
 *   error, DNS, abort), we leave the badge unchanged — a stale-but-
 *   possibly-correct number beats a fabricated zero, because the next
 *   inbound push would otherwise restore the stale count and reproduce
 *   Paul's 2026-04-19 regression ("badge shows (3), inbox empty"). `fetch`
 *   does not throw on non-2xx, so the response must be explicitly
 *   inspected; a swallowed catch would otherwise silently proceed.
 *
 *   Enforced by
 *   tests/gate/test_notification_read_state.py::
 *     test_mark_chat_as_read_gates_badge_clear_on_backend_ack
 *     test_mark_chat_as_read_preserves_badge_on_network_error.
 */
export async function markChatAsRead(): Promise<void> {
  // 1. Tell the backend the chat has been viewed, and record whether
  //    the backend actually acknowledged. Only a true ack unlocks the
  //    native-side clears below.
  let markReadAckd = false;

  try {
    // fetchWithAuth, not a hand-read token: a snapshot JWT that expired while
    // the app sat in the background 401s, and the read pointer then silently
    // stops advancing while the badge keeps being restored from the stale
    // server-side count — the 2026-04-19 symptom by a different route.
    // fetchWithAuth refreshes proactively and retries once on 401. With no
    // token at all it returns a synthetic 401 (no network call), so `res.ok`
    // is false and the badge clear below is correctly skipped, exactly as the
    // old `if (token)` guard did.
    //
    // ⚠️ NEVER QUEUE OR REPLAY THIS REQUEST. It is idempotent in the trivial
    // sense — POSTing twice sets the same pointer — but its correctness is
    // TIME-DEPENDENT: the server stamps `chat_last_read_at` at RECEIPT time.
    // Replaying it hours later marks messages that arrived in the interim as
    // read, silently burying genuinely-unread coaching. This is the writer
    // where "idempotent" and "safe to replay" come apart; a failed mark-read
    // must simply be dropped and re-issued by the next chat-screen focus.
    const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/mark-chat-read`, {
      method: 'POST',
    });
    if (res.ok) {
      markReadAckd = true;
    }
    // Non-2xx: leave markReadAckd = false so the badge clear below
    // is skipped. No logging for the common "unauthenticated / trial
    // expired" 401 to keep the console quiet on the public pages.
  } catch {
    // Network/DNS/abort — same Pattern B contract as non-2xx. Explicit
    // early-return so control cannot reach the native clears on a
    // swallowed failure.
    return;
  }

  if (!markReadAckd) return;

  // Native-only badge + Notification-Centre cleanup. Web has no icon
  // badge or notification centre, so these are no-ops on web.
  if (Platform.OS === 'web') return;

  try {
    await Notifications.setBadgeCountAsync(0);
  } catch {
    // Non-fatal. Backend is already aligned; a local-clear failure
    // self-heals on the next AppState 'active' → syncBadgeToBackend.
  }

  try {
    await Notifications.dismissAllNotificationsAsync();
  } catch {
    // Non-fatal; same rationale.
  }
}
